import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import * as signalR from "@microsoft/signalr";
import { useAuth } from "../firebase/AuthProvider";
import { toast } from 'react-toastify';
import { ValidateOtpResponseDto } from "../interfaces/ExtermalUserDtos/ExternalUsersDtos";
import EventNotification from "../interfaces/eventNotification/EventNotification";
import { useNotifications } from "./NotificationProvider";
import { MessageBuilderFromEventNotification } from "../utilities/notifications/EventNotificationUtils";
import { MessageSendResponse } from "../interfaces/conversationDtos/conversationdto";
import { Subscription } from "rxjs";
import { currentConversationId$ } from "../services/Subjects/userSubject";
import { useNotificationState } from "./NotificationStateContext";
import ChatMessageNotification from "../components/components/notifications/ChatMessageNotification";
import ToastNotification from '../components/components/notifications/ToastNotification';

/**
 * The shape of our context: a single HubConnection or null.
 */
interface ChatConnectionContextType {
    connection: signalR.HubConnection | null;
    resetConnection: (externalUserDetails: ValidateOtpResponseDto) => void;
}

const ChatConnectionContext = createContext<ChatConnectionContextType | null>(null);

// const ChatConnectionContext = createContext<signalR.HubConnection | null>(null);

interface ChatConnectionProviderProps {
    children: React.ReactNode;
}

export const ChatConnectionProvider: React.FC<ChatConnectionProviderProps> = ({
    children,
}) => {

    const { notify, notifyForCall } = useNotifications();
    const { currentUser } = useAuth();
    const [connection, setConnection] = useState<signalR.HubConnection | null>(null);
    const [externalUser, setExternalUser] = useState<ValidateOtpResponseDto>();
    const [showChatBanner, setShowChatBanner] = useState<boolean>(false);
    const [currentBannerData, setCurrentBannerData] = useState<MessageSendResponse>();
    const currentConversationIdRef = useRef(0);
    const {addNewNotification} = useNotificationState();

    useEffect(() => {
        if (connection) return; // Prevent creating multiple connections
        if (!externalUser && !currentUser) return; // prevent for if both users not present

        let subscription: Subscription;

        subscription = currentConversationId$.subscribe({
            next: (conversationId) => {
                currentConversationIdRef.current = conversationId;
            }
        })

        const getNewConn = async () => {
            console.log("Connection start");

            const authToken: string = currentUser ? await currentUser.getIdToken() : "";

            let hubUrl = `${process.env.REACT_APP_API_BASE_URL}/chatHub`;

            if (externalUser?.tempSessionToken) {
                hubUrl = `${process.env.REACT_APP_API_BASE_URL}/chatHub?videoToken=${externalUser.tempSessionToken}&ticketId=${externalUser.ticketId}&userId=${externalUser.externalUserDetailId}`
            }

            const newConn = new signalR.HubConnectionBuilder()
                .withUrl(hubUrl, {
                    accessTokenFactory: async () => currentUser ? await currentUser.getIdToken(true) : "",
                })
                .withAutomaticReconnect()
                .configureLogging(signalR.LogLevel.Information)
                .build();

            // for notification Servcie
            newConn.on(
                "receivenotification_web",
                (response: EventNotification) => {
                    console.log("Log: Notification event from ChatConnectionContext.tsx:receivenotification_web", { response });
                    notify(<ToastNotification notification={response} />, {
                        toastId: response.id,
                        className: 'custom-toast-wrapper p-0',
                        closeButton: false,
                        hideProgressBar: true,
                    });
                    // Add notification in list
                    addNewNotification(response);
                }
            )

            // notification for chat messages
                        newConn.on(
                            "ReceiveMessageForUserASNotification",
                            (response: MessageSendResponse) => {
                                console.log("Log: Chat notification event from ChatConnectionContext.tsx:ReceiveMessageForUserASNotification", { response });
                                // console.log("Chat notification : ", response);
                                if (currentConversationIdRef.current != response.conversationId) {
                                    // notify(response.messageContent);
                                    setCurrentBannerData(response);
                                    setShowChatBanner(true);
                                    setTimeout(() => {
                                        setShowChatBanner(false);
                                    }, 5000);
                                }
                            }
                        )
            try {
                console.log("[Global] Connecting.");
                await newConn.start();
                console.log("[Global] SignalR connection started.");

                newConn.on("ReceiveReminderNotification", (data) => {
                    console.log("Log: Raw toast from ChatConnectionContext.tsx:ReceiveReminderNotification", { data });
                    toast.info(`${data.title}\n${data.message}`);

                });

                newConn.on("NewMessage", (conversation) => {
                    console.log("[Notification] New Message received:", conversation);
                    toast.info(`You have a new message in conversation ${conversation.id}`);
                });
                setConnection(newConn);
            } catch (err) {
                console.error("[Global] Failed to connect SignalR:", err);
            }
        };

        getNewConn();

        return () => {
            setConnection((prevConnection) => {
                if (prevConnection) {
                    prevConnection
                        .stop()
                        .then(() => console.log("[Global] SignalR connection stopped."))
                        .catch(console.error);
                }
                return null; // Ensure state resets
            });

            if(subscription){
                subscription.unsubscribe();
            }
        };

    }, [currentUser, externalUser]);

    

    const resetConnection = (externalUserDetails: ValidateOtpResponseDto) => {
        console.log("Connection reset");

        setExternalUser(externalUserDetails);
    }

    return (
        <ChatConnectionContext.Provider value={{ connection, resetConnection }}>
            {children}

        </ChatConnectionContext.Provider>
    );
};

/**
 * A small helper hook that returns our global chat connection (or null).
 */
// export function useChatConnection(): signalR.HubConnection | null {
//   return useContext(ChatConnectionContext);
// }
export function useChatConnection(): ChatConnectionContextType {
    const context = useContext(ChatConnectionContext);
    if (!context) {
        throw new Error("useChatConnection must be used within ChatConnectionProvider");
    }
    return context;
}
